// Cycle Explorer: select mesh Nodes, then either Histogram Analysis
// (water-level histogram across cycles) or Multi-Parameter (per-cycle
// dual-axis time series for one Node).

import {buildQuadtreeIndex} from './quadtree.js';
import {TileStore, FILL_VALUE} from './tile-store.js';

const MAX_NODES = 15;
const CLICK_SLOP_PX = 6;
const FETCH_CONCURRENCY = 8;
const MATCH_DIST_DEG = 0.02;
const FALLBACK_MATCH_DIST_DEG = 0.1;
const MP_FIELDS = {
  waterlevel: {
    id: 'waterlevel',
    label: 'Water Level',
    axisTitle: 'Water level (m)',
    unit: 'm',
    kind: 'scalar'
  },
  waterspeed: {
    id: 'waterspeed',
    label: 'Water Velocity',
    axisTitle: 'Water velocity (m/s)',
    unit: 'm/s',
    kind: 'speed',
    vectorField: 'velocity',
    manifestFlag: 'has_velocity'
  },
  windspeed: {
    id: 'windspeed',
    label: 'Wind Velocity',
    axisTitle: 'Wind velocity (m/s)',
    unit: 'm/s',
    kind: 'speed',
    vectorField: 'wind',
    manifestFlag: 'has_wind'
  }
};
const NODE_COLORS = [
  [212, 160, 23, 240],
  [180, 130, 20, 240],
  [245, 190, 60, 240],
  [160, 120, 30, 240],
  [230, 170, 40, 240],
  [140, 100, 20, 240],
  [250, 210, 80, 240],
  [190, 145, 35, 240],
  [170, 125, 25, 240],
  [220, 175, 50, 240],
  [200, 150, 30, 240],
  [155, 115, 22, 240],
  [235, 185, 55, 240],
  [175, 135, 28, 240],
  [210, 165, 45, 240]
];

class AnomalyAbortError extends Error {
  constructor(message = 'Anomaly fetch cancelled') {
    super(message);
    this.name = 'AnomalyAbortError';
  }
}

function wrapLon(lon) {
  let x = lon;
  while (x > 180) x -= 360;
  while (x < -180) x += 360;
  return x;
}

function unwrapLonNear(lon, refLon) {
  let x = lon;
  while (x - refLon > 180) x -= 360;
  while (x - refLon < -180) x += 360;
  return x;
}

function vertexDistSq(lon, lat, pos, index) {
  let dx = unwrapLonNear(pos[index * 3], lon) - lon;
  const dy = pos[index * 3 + 1] - lat;
  return dx * dx + dy * dy;
}

function candidateLeafTiles(index, lon, lat, padDeg = 0.25) {
  if (!index?.leafSet) return [];
  const hits = [];
  for (const key of index.leafSet) {
    const bbox = index.leafBBox.get(key);
    if (!bbox) continue;
    const [w, s, e, n] = bbox;
    if (lat < s - padDeg || lat > n + padDeg) continue;
    // Node lon is wrapped to [-180, 180] but tile bboxes may use 0..360.
    if (![0, -360, 360].some(o => lon + o >= w - padDeg && lon + o <= e + padDeg)) {
      continue;
    }
    const [z, x, y] = key.split(',').map(Number);
    hits.push({z, x, y, key, area: (e - w) * (n - s)});
  }
  hits.sort((a, b) => b.z - a.z || a.area - b.area);
  return hits;
}

function nearestVertexInEntry(entry, lon, lat) {
  const pos = entry.positions3;
  if (!pos) return null;
  const n = entry.nVerts || pos.length / 3;
  let best = null;
  for (let i = 0; i < n; i++) {
    const distSq = vertexDistSq(lon, lat, pos, i);
    if (!best || distSq < best.distSq) {
      best = {
        vertexIndex: i,
        distSq,
        lon: pos[i * 3],
        lat: pos[i * 3 + 1]
      };
    }
  }
  return best;
}

async function mapPool(items, concurrency, fn, {isAborted} = {}) {
  const results = new Array(items.length);
  let next = 0;
  async function worker() {
    while (true) {
      if (isAborted?.()) throw new AnomalyAbortError();
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }
  const n = Math.max(1, Math.min(concurrency, items.length || 1));
  await Promise.all(Array.from({length: n}, () => worker()));
  return results;
}

function cycleHour(cycle) {
  const parts = String(cycle).split('_');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function formatCycleLabel(cycle) {
  const [date, hh] = String(cycle).split('_');
  if (!date || hh == null) return String(cycle);
  return `${date} ${hh}z`;
}

function formatTimestepLabel(ts) {
  const s = String(ts || '').trim();
  if (!s) return '—';
  return s.replace(/:\d{2}$/, '');
}

function isoTimestep(ts) {
  return String(ts).trim().replace(' ', 'T');
}

function cycleStartMs(cycle) {
  const [date, hh] = String(cycle).split('_');
  if (!date) return NaN;
  const [y, mo, d] = date.split('-').map(Number);
  const hour = Number(hh);
  if (![y, mo, d, hour].every(Number.isFinite)) return NaN;
  return Date.UTC(y, mo - 1, d, hour, 0, 0);
}

function timestepMs(ts) {
  const iso = isoTimestep(ts);
  const ms = Date.parse(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isFinite(ms) ? ms : NaN;
}

/** Forecast lead time in hours relative to cycle init. */
function leadHours(cycle, timestep) {
  const start = cycleStartMs(cycle);
  const t = timestepMs(timestep);
  if (!Number.isFinite(start) || !Number.isFinite(t)) return null;
  return (t - start) / 3600000;
}

function formatLeadHours(h) {
  if (!Number.isFinite(h)) return '—';
  const rounded = Math.round(h * 10) / 10;
  const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `+${s} h`;
}

function computeSampleStats(samples) {
  const vals = (samples || []).filter(s => Number.isFinite(s?.value));
  if (!vals.length) return null;
  let minS = vals[0];
  let maxS = vals[0];
  let sum = 0;
  for (const s of vals) {
    sum += s.value;
    if (s.value < minS.value) minS = s;
    if (s.value > maxS.value) maxS = s;
  }
  const mean = sum / vals.length;
  let varSum = 0;
  for (const s of vals) {
    const d = s.value - mean;
    varSum += d * d;
  }
  const std = Math.sqrt(varSum / vals.length);
  return {min: minS, max: maxS, mean, std, n: vals.length};
}

function formatStatSample(sample) {
  if (!sample || !Number.isFinite(sample.value)) return '—';
  const cycle = formatCycleLabel(sample.cycle);
  const lead = formatLeadHours(sample.leadHours);
  return `${sample.value.toFixed(3)} m  ·  ${cycle}  ·  lead ${lead}`;
}

function renderHistStats(el, samples) {
  if (!el) return;
  const stats = computeSampleStats(samples);
  if (!stats) {
    el.classList.add('hidden');
    el.innerHTML = '';
    return;
  }
  el.classList.remove('hidden');
  el.innerHTML =
    `<div class="stats-title">Aggregated statistics (${stats.n.toLocaleString()} samples)</div>` +
    `<dl class="stats-grid">` +
    `<dt>Min</dt><dd>${formatStatSample(stats.min)}</dd>` +
    `<dt>Max</dt><dd>${formatStatSample(stats.max)}</dd>` +
    `<dt>Mean</dt><dd>${stats.mean.toFixed(3)} m</dd>` +
    `<dt>Std</dt><dd>${stats.std.toFixed(3)} m</dd>` +
    `</dl>`;
}

function renderHistogram(plotEl, values, {title, subtitle, markerValues = []} = {}) {
  const clean = (values || []).filter(v => Number.isFinite(v));
  if (!clean.length) {
    Plotly.purge(plotEl);
    return {empty: true, message: 'No wet (non-fill) water-level samples for this view.'};
  }
  const uniqMarkers = [];
  for (const v of markerValues) {
    if (!Number.isFinite(v)) continue;
    if (uniqMarkers.some(u => Math.abs(u - v) < 1e-6)) continue;
    uniqMarkers.push(v);
  }

  const baseAnnotations = [];
  if (subtitle) {
    baseAnnotations.push({
      text: subtitle,
      xref: 'paper',
      yref: 'paper',
      x: 1,
      y: 1.12,
      xanchor: 'right',
      yanchor: 'bottom',
      showarrow: false,
      font: {size: 11, color: '#64748b'}
    });
  }

  const layout = {
    margin: {t: 48, r: 16, b: 48, l: 56},
    height: 340,
    autosize: true,
    title: {
      text: title || 'Water level histogram',
      font: {size: 13, color: '#0f172a'},
      x: 0,
      xanchor: 'left'
    },
    xaxis: {title: 'Total water level (m)'},
    yaxis: {title: 'Count', rangemode: 'tozero'},
    bargap: 0.05,
    annotations: baseAnnotations,
    shapes: [],
    showlegend: false
  };

  const histTrace = {
    type: 'histogram',
    x: clean,
    marker: {color: '#c9a227', line: {color: '#8a7014', width: 0.5}},
    nbinsx: Math.min(60, Math.max(12, Math.round(Math.sqrt(clean.length)))),
    hovertemplate: 'bin %{x}<br>count %{y}<extra></extra>',
    name: 'Water level'
  };

  Plotly.react(plotEl, [histTrace], layout, {responsive: true, displayModeBar: false})
    .then(() => {
      // Read bin heights so vertical lines can use the same y-axis as the bars.
      let yMax = 0;
      const calc = plotEl.calcdata?.[0];
      if (Array.isArray(calc)) {
        for (const bin of calc) {
          const y = bin?.y;
          if (Number.isFinite(y) && y > yMax) yMax = y;
        }
      }
      if (!(yMax > 0)) {
        // Fallback if calcdata is unavailable.
        yMax = Math.max(1, Math.ceil(clean.length / Math.max(1, Math.round(Math.sqrt(clean.length)))));
      }

      const shapes = uniqMarkers.map(v => ({
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: v,
        x1: v,
        y0: 0,
        y1: yMax,
        line: {color: '#dc2626', width: 3},
        layer: 'above'
      }));

      const annotations = baseAnnotations.slice();
      if (uniqMarkers.length === 1) {
        annotations.push({
          x: uniqMarkers[0],
          y: yMax,
          xref: 'x',
          yref: 'y',
          text: `current ${uniqMarkers[0].toFixed(3)} m`,
          showarrow: false,
          yanchor: 'bottom',
          xanchor: 'left',
          xshift: 4,
          font: {size: 11, color: '#dc2626', family: 'sans-serif'}
        });
      } else if (uniqMarkers.length > 1) {
        annotations.push({
          text: `current view (${uniqMarkers.length} Nodes)`,
          xref: 'paper',
          yref: 'paper',
          x: 0.02,
          y: 0.92,
          xanchor: 'left',
          yanchor: 'top',
          showarrow: false,
          font: {size: 11, color: '#dc2626'}
        });
      }

      return Plotly.relayout(plotEl, {shapes, annotations, 'yaxis.range': [0, yMax * 1.08]});
    })
    .then(() => {
      requestAnimationFrame(() => Plotly.Plots.resize(plotEl));
    })
    .catch(err => console.warn('[cycle-explorer] histogram marker overlay failed', err));

  return {empty: false, n: clean.length, marked: uniqMarkers.length};
}

function renderMultiParamPlot(plotEl, {title, times, left, right, leftField, rightField}) {
  const leftColor = '#2563eb';
  const rightColor = '#16a34a';
  const traces = [
    {
      x: times,
      y: left,
      type: 'scatter',
      mode: 'lines',
      name: leftField.label,
      line: {color: leftColor, width: 2},
      connectgaps: false,
      yaxis: 'y'
    },
    {
      x: times,
      y: right,
      type: 'scatter',
      mode: 'lines',
      name: rightField.label,
      line: {color: rightColor, width: 2},
      connectgaps: false,
      yaxis: 'y2'
    }
  ];
  const layout = {
    margin: {t: 36, r: 56, b: 48, l: 56},
    height: 280,
    autosize: true,
    title: {text: title, font: {size: 12, color: '#0f172a'}, x: 0, xanchor: 'left'},
    xaxis: {title: 'Date/Time', type: 'date'},
    yaxis: {title: leftField.axisTitle, side: 'left', color: leftColor},
    yaxis2: {
      title: rightField.axisTitle,
      overlaying: 'y',
      side: 'right',
      color: rightColor
    },
    legend: {orientation: 'h', y: 1.12, x: 1, xanchor: 'right'},
    showlegend: true
  };
  return Plotly.react(plotEl, traces, layout, {responsive: true, displayModeBar: false});
}

function createDualRange(cfg) {
  let items = [];

  function syncFill() {
    const max = Math.max(0, parseInt(cfg.minInput.max, 10) || 0);
    if (max <= 0) {
      cfg.fillEl.style.left = '0%';
      cfg.fillEl.style.width = '100%';
      return;
    }
    let a = parseInt(cfg.minInput.value, 10) || 0;
    let b = parseInt(cfg.maxInput.value, 10) || 0;
    if (a > b) [a, b] = [b, a];
    const left = (a / max) * 100;
    const right = (b / max) * 100;
    cfg.fillEl.style.left = `${left}%`;
    cfg.fillEl.style.width = `${Math.max(0, right - left)}%`;
  }

  function updateLabels() {
    let a = parseInt(cfg.minInput.value, 10) || 0;
    let b = parseInt(cfg.maxInput.value, 10) || 0;
    if (a > b) [a, b] = [b, a];
    const startItem = items[a];
    const endItem = items[b];
    if (cfg.startLabelEl) {
      cfg.startLabelEl.textContent = startItem != null ? cfg.formatLabel(startItem, a) : '—';
    }
    if (cfg.endLabelEl) {
      cfg.endLabelEl.textContent = endItem != null ? cfg.formatLabel(endItem, b) : '—';
    }
    if (cfg.oldestEl) {
      cfg.oldestEl.textContent = items.length ? cfg.formatLabel(items[0], 0) : '—';
    }
    if (cfg.newestEl) {
      cfg.newestEl.textContent = items.length
        ? cfg.formatLabel(items[items.length - 1], items.length - 1)
        : '—';
    }
    syncFill();
  }

  function setItems(nextItems, {reset = true} = {}) {
    items = Array.isArray(nextItems) ? nextItems.slice() : [];
    const max = Math.max(0, items.length - 1);
    cfg.minInput.min = '0';
    cfg.maxInput.min = '0';
    cfg.minInput.max = String(max);
    cfg.maxInput.max = String(max);
    if (reset || parseInt(cfg.minInput.value, 10) > max) cfg.minInput.value = '0';
    if (reset || parseInt(cfg.maxInput.value, 10) > max) cfg.maxInput.value = String(max);
    let a = parseInt(cfg.minInput.value, 10) || 0;
    let b = parseInt(cfg.maxInput.value, 10) || 0;
    if (a > b) {
      cfg.minInput.value = String(b);
      cfg.maxInput.value = String(a);
    }
    cfg.minInput.disabled = items.length === 0;
    cfg.maxInput.disabled = items.length === 0;
    updateLabels();
  }

  function getRange() {
    let a = parseInt(cfg.minInput.value, 10) || 0;
    let b = parseInt(cfg.maxInput.value, 10) || 0;
    if (a > b) [a, b] = [b, a];
    return {startIndex: a, endIndex: b, items: items.slice(a, b + 1)};
  }

  function onInput(which) {
    let a = parseInt(cfg.minInput.value, 10) || 0;
    let b = parseInt(cfg.maxInput.value, 10) || 0;
    if (which === 'min' && a > b) cfg.minInput.value = String(b);
    if (which === 'max' && b < a) cfg.maxInput.value = String(a);
    updateLabels();
  }

  cfg.minInput.addEventListener('input', () => onInput('min'));
  cfg.maxInput.addEventListener('input', () => onInput('max'));

  return {setItems, getRange, updateLabels};
}

/**
 * @param {object} host callbacks into app.js
 */
export function initAnomalyDetection(host) {
  const btn = document.getElementById('anomalyDetectionBtn');
  const modal = document.getElementById('anomalyModal');
  if (!btn || !modal) return;

  const closeBtn = document.getElementById('anomalyModalClose');
  const cancelBtn = document.getElementById('anomalyCancelBtn');
  const selectBtn = document.getElementById('anomalySelectBtn');
  const clearBtn = document.getElementById('anomalyClearBtn');
  const histRunBtn = document.getElementById('anomalyHistRunBtn');
  const mpRunBtn = document.getElementById('anomalyMpRunBtn');
  const statusEl = document.getElementById('anomalyStatus');
  const countEl = document.getElementById('anomalyNodeCount');
  const banner = document.getElementById('anomalySelectBanner');
  const bannerText = document.getElementById('anomalySelectBannerText');
  const selectDoneBtn = document.getElementById('anomalySelectDoneBtn');
  const viewModeSel = document.getElementById('anomalyViewMode');
  const plotEl = document.getElementById('anomalyPlot');
  const plotStatusEl = document.getElementById('anomalyPlotStatus');
  const histStatsEl = document.getElementById('anomalyHistStats');
  const resultsEl = document.getElementById('anomalyResults');
  const cycleTypeSel = document.getElementById('anomalyCycleType');
  const tabHistorical = document.getElementById('anomalyTabHistorical');
  const tabMultiparam = document.getElementById('anomalyTabMultiparam');
  const panelHistorical = document.getElementById('anomalyPanelHistorical');
  const panelMultiparam = document.getElementById('anomalyPanelMultiparam');
  const mpNodeSelect = document.getElementById('anomalyMpNodeSelect');
  const mpLeftAxisSel = document.getElementById('anomalyMpLeftAxis');
  const mpRightAxisSel = document.getElementById('anomalyMpRightAxis');
  const mpCycleTypeSel = document.getElementById('anomalyMpCycleType');
  const mpResultsEl = document.getElementById('anomalyMpResults');
  const mpPlotsEl = document.getElementById('anomalyMpPlots');
  const mpPlotStatusEl = document.getElementById('anomalyMpPlotStatus');

  const cycleRange = createDualRange({
    minInput: document.getElementById('anomalyCycleStart'),
    maxInput: document.getElementById('anomalyCycleEnd'),
    fillEl: document.getElementById('anomalyCycleRangeFill'),
    oldestEl: document.getElementById('anomalyCycleOldest'),
    newestEl: document.getElementById('anomalyCycleNewest'),
    startLabelEl: document.getElementById('anomalyCycleStartLabel'),
    endLabelEl: document.getElementById('anomalyCycleEndLabel'),
    formatLabel: c => formatCycleLabel(c)
  });

  const timeRange = createDualRange({
    minInput: document.getElementById('anomalyTimeStart'),
    maxInput: document.getElementById('anomalyTimeEnd'),
    fillEl: document.getElementById('anomalyTimeRangeFill'),
    oldestEl: document.getElementById('anomalyTimeOldest'),
    newestEl: document.getElementById('anomalyTimeNewest'),
    startLabelEl: document.getElementById('anomalyTimeStartLabel'),
    endLabelEl: document.getElementById('anomalyTimeEndLabel'),
    formatLabel: ts => formatTimestepLabel(ts)
  });

  const mpCycleRange = createDualRange({
    minInput: document.getElementById('anomalyMpCycleStart'),
    maxInput: document.getElementById('anomalyMpCycleEnd'),
    fillEl: document.getElementById('anomalyMpCycleRangeFill'),
    oldestEl: document.getElementById('anomalyMpCycleOldest'),
    newestEl: document.getElementById('anomalyMpCycleNewest'),
    startLabelEl: document.getElementById('anomalyMpCycleStartLabel'),
    endLabelEl: document.getElementById('anomalyMpCycleEndLabel'),
    formatLabel: c => formatCycleLabel(c)
  });

  const mpTimeRange = createDualRange({
    minInput: document.getElementById('anomalyMpTimeStart'),
    maxInput: document.getElementById('anomalyMpTimeEnd'),
    fillEl: document.getElementById('anomalyMpTimeRangeFill'),
    oldestEl: document.getElementById('anomalyMpTimeOldest'),
    newestEl: document.getElementById('anomalyMpTimeNewest'),
    startLabelEl: document.getElementById('anomalyMpTimeStartLabel'),
    endLabelEl: document.getElementById('anomalyMpTimeEndLabel'),
    formatLabel: ts => formatTimestepLabel(ts)
  });

  let activeTab = 'historical'; // 'historical' | 'multiparam'
  let selecting = false;
  let fetching = false;
  let abortRequested = false;
  let nodes = []; // {id, lon, lat}
  let nextId = 1;
  /** @type {null | {byNode: Map<number, number[]>, all: number[], currentByNode: Map<number, number>, meta: object}} */
  let histResult = null;
  /** @type {null | {nodeId: number, cycles: Array<{cycle, times, water, wind}>}} */
  let mpResult = null;
  let focusNodeId = null;

  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || '';
  }

  function setPlotStatus(el, msg, {error = false} = {}) {
    if (!el) return;
    if (!msg) {
      el.classList.add('hidden');
      el.textContent = '';
      return;
    }
    el.classList.remove('hidden');
    el.textContent = msg;
    el.classList.toggle('error', error);
  }

  function markerPayload() {
    return nodes.map(n => ({
      ...n,
      focused: focusNodeId != null && n.id === focusNodeId
    }));
  }

  function allCyclesAsc() {
    const cycles = host.getActiveCycles?.() || [];
    return [...cycles].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }

  function filterCycles(type) {
    const all = allCyclesAsc();
    return type === 'all' ? all : all.filter(c => cycleHour(c) === type);
  }

  function refreshHistoricalOptions({resetRange = true} = {}) {
    cycleRange.setItems(filterCycles(cycleTypeSel?.value || 'all'), {reset: resetRange});
    timeRange.setItems(host.getTimesteps?.() || [], {reset: resetRange});
  }

  function refreshMpOptions({resetRange = true} = {}) {
    mpCycleRange.setItems(filterCycles(mpCycleTypeSel?.value || 'all'), {reset: resetRange});
    mpTimeRange.setItems(host.getTimesteps?.() || [], {reset: resetRange});
  }

  function rebuildMpNodeSelect() {
    if (!mpNodeSelect) return;
    const prev = mpNodeSelect.value;
    mpNodeSelect.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = nodes.length ? 'Select a Node…' : 'Select Nodes in Step 1 first';
    mpNodeSelect.appendChild(placeholder);
    if (nodes.length > 0) {
      const avg = document.createElement('option');
      avg.value = 'average';
      avg.textContent = 'Average of Selected Nodes';
      mpNodeSelect.appendChild(avg);
    }
    for (const n of nodes) {
      const opt = document.createElement('option');
      opt.value = String(n.id);
      opt.textContent = `Node #${n.id}  (${n.lat.toFixed(4)}, ${n.lon.toFixed(4)})`;
      mpNodeSelect.appendChild(opt);
    }
    if (prev && [...mpNodeSelect.options].some(o => o.value === prev)) {
      mpNodeSelect.value = prev;
    } else if (focusNodeId != null && nodes.some(n => n.id === focusNodeId)) {
      mpNodeSelect.value = String(focusNodeId);
    }
  }

  function rebuildViewModeOptions() {
    if (!viewModeSel) return;
    const prev = viewModeSel.value;
    viewModeSel.innerHTML = '';
    const agg = document.createElement('option');
    agg.value = 'aggregate';
    agg.textContent = 'Aggregate (all selected Nodes)';
    viewModeSel.appendChild(agg);
    for (const n of nodes) {
      const opt = document.createElement('option');
      opt.value = String(n.id);
      opt.textContent = `Node #${n.id}  (${n.lat.toFixed(4)}, ${n.lon.toFixed(4)})`;
      viewModeSel.appendChild(opt);
    }
    if (prev && [...viewModeSel.options].some(o => o.value === prev)) {
      viewModeSel.value = prev;
    } else if (focusNodeId != null) {
      viewModeSel.value = String(focusNodeId);
    } else {
      viewModeSel.value = 'aggregate';
    }
  }

  function updateRunButton() {
    if (histRunBtn) {
      histRunBtn.disabled = nodes.length === 0 || fetching || selecting;
    }
    if (mpRunBtn) {
      const sel = mpNodeSelect?.value || '';
      const nodeOk = sel === 'average' ? nodes.length > 0 : Boolean(sel);
      mpRunBtn.disabled = nodes.length === 0 || !nodeOk || fetching || selecting;
    }
  }

  function setActiveTab(tab) {
    activeTab = tab === 'multiparam' ? 'multiparam' : 'historical';
    tabHistorical?.classList.toggle('active', activeTab === 'historical');
    tabMultiparam?.classList.toggle('active', activeTab === 'multiparam');
    panelHistorical?.classList.toggle('hidden', activeTab !== 'historical');
    panelMultiparam?.classList.toggle('hidden', activeTab !== 'multiparam');
    updateRunButton();
  }

  function updateNodeUi() {
    if (countEl) countEl.textContent = `${nodes.length} / ${MAX_NODES}`;
    if (clearBtn) clearBtn.disabled = nodes.length === 0 || fetching || selecting;
    if (selectBtn) selectBtn.disabled = fetching;
    if (cycleTypeSel) cycleTypeSel.disabled = fetching;
    if (mpCycleTypeSel) mpCycleTypeSel.disabled = fetching;
    if (mpNodeSelect) mpNodeSelect.disabled = fetching;
    if (mpLeftAxisSel) mpLeftAxisSel.disabled = fetching;
    if (mpRightAxisSel) mpRightAxisSel.disabled = fetching;
    host.setAnomalyMarkers?.(markerPayload());
    rebuildViewModeOptions();
    rebuildMpNodeSelect();
    updateRunButton();
  }

  function resolveCurrentMarkerValues(mode) {
    if (!histResult) return [];
    const viewCycle = host.getForecastCycle?.() || histResult.meta?.viewCycle || '';
    const viewTsIdx = host.getTimestepIndex?.() ?? histResult.meta?.viewTimestep ?? -1;
    const viewTsLabel = (host.getTimesteps?.() || [])[viewTsIdx];

    const matchSamples = samples => {
      const out = [];
      for (const s of samples || []) {
        if (!Number.isFinite(s?.value)) continue;
        if (viewCycle && s.cycle !== viewCycle) continue;
        if (viewTsLabel != null && String(s.timestep) !== String(viewTsLabel)) continue;
        out.push(s.value);
      }
      return out;
    };

    if (mode === 'aggregate') {
      const fromMap = [...(histResult.currentByNode?.values?.() || [])].filter(Number.isFinite);
      if (fromMap.length) return fromMap;
      return matchSamples(histResult.all);
    }

    const id = parseInt(mode, 10);
    if (histResult.currentByNode?.has(id)) {
      const v = histResult.currentByNode.get(id);
      return Number.isFinite(v) ? [v] : [];
    }
    return matchSamples(histResult.byNode.get(id) || []);
  }

  function drawCurrentHistogram() {
    if (!histResult || !plotEl) return;
    const mode = viewModeSel?.value || 'aggregate';
    let samples;
    let title;
    if (mode === 'aggregate') {
      samples = histResult.all;
      title = 'Aggregate water level';
      focusNodeId = null;
    } else {
      const id = parseInt(mode, 10);
      samples = histResult.byNode.get(id) || [];
      title = `Node #${id} water level`;
      focusNodeId = id;
    }
    const markerValues = resolveCurrentMarkerValues(mode);
    const values = samples.map(s => s.value);
    host.setAnomalyMarkers?.(markerPayload());
    updateNodeUi();
    const nFin = values.filter(Number.isFinite).length;
    const subtitle =
      `${nFin.toLocaleString()} samples · ${histResult.meta.cyclesOk}/${histResult.meta.cyclesTotal} cycles` +
      (markerValues.length ? '' : ' · current view not in selection');
    const out = renderHistogram(plotEl, values, {title, subtitle, markerValues});
    if (out.empty) {
      const d = histResult.meta.diag;
      const detail = d
        ? ` (unresolved node/cycle: ${d.unresolved}, dry/fill reads: ${d.dry}, failed tile reads: ${d.failed})`
        : '';
      setPlotStatus(plotStatusEl, out.message + detail, {error: true});
      renderHistStats(histStatsEl, null);
    } else {
      setPlotStatus(plotStatusEl, '');
      renderHistStats(histStatsEl, samples);
    }
  }

  function clearMpPlots() {
    if (!mpPlotsEl) return;
    for (const child of [...mpPlotsEl.children]) {
      try {
        Plotly.purge(child);
      } catch (_) {
        /* ignore */
      }
    }
    mpPlotsEl.innerHTML = '';
  }

  function drawMpPlots() {
    if (!mpResult || !mpPlotsEl) return;
    clearMpPlots();
    const leftField = mpResult.leftField;
    const rightField = mpResult.rightField;
    for (const series of mpResult.cycles) {
      const wrap = document.createElement('div');
      const title = document.createElement('div');
      title.className = 'anomaly-mp-plot-title';
      title.textContent = formatCycleLabel(series.cycle);
      const plot = document.createElement('div');
      plot.className = 'anomaly-mp-plot';
      wrap.appendChild(title);
      wrap.appendChild(plot);
      mpPlotsEl.appendChild(wrap);
      renderMultiParamPlot(plot, {
        title:
          (mpResult.mode === 'average'
            ? `Average of ${mpResult.nodeCount} Nodes`
            : `Node #${mpResult.nodeId}`) + ` · ${formatCycleLabel(series.cycle)}`,
        times: series.times,
        left: series.left,
        right: series.right,
        leftField,
        rightField
      }).then(() => {
        requestAnimationFrame(() => Plotly.Plots.resize(plot));
      });
    }
  }

  function openModal() {
    if (!host.getManifest?.()) {
      setStatus('Load a forecast cycle first.');
      modal.classList.remove('hidden');
      return;
    }
    refreshHistoricalOptions({resetRange: true});
    refreshMpOptions({resetRange: true});
    setActiveTab(activeTab);
    modal.classList.remove('hidden');
    updateNodeUi();
    setStatus('');
  }

  function stopSelectMode() {
    selecting = false;
    host.setAnomalySelectMode?.(false);
    banner?.classList.remove('visible');
    modal.classList.remove('hidden');
    updateNodeUi();
  }

  function closeModal() {
    if (fetching) return;
    selecting = false;
    host.setAnomalySelectMode?.(false);
    banner?.classList.remove('visible');

    nodes = [];
    nextId = 1;
    histResult = null;
    mpResult = null;
    focusNodeId = null;
    resultsEl?.classList.add('hidden');
    mpResultsEl?.classList.add('hidden');
    if (histStatsEl) {
      histStatsEl.classList.add('hidden');
      histStatsEl.innerHTML = '';
    }
    try {
      if (plotEl) Plotly.purge(plotEl);
    } catch (_) {
      /* empty */
    }
    clearMpPlots();
    setPlotStatus(plotStatusEl, '');
    setPlotStatus(mpPlotStatusEl, '');
    setStatus('');
    host.setAnomalyMarkers?.([]);
    updateNodeUi();
    modal.classList.add('hidden');
  }

  function ensureMeshEdgesOn() {
    if (!host.getShowEdges?.()) {
      host.setShowEdges?.(true);
      setStatus('Show Mesh Edges turned on for Node picking.');
    }
  }

  function startSelectMode() {
    if (fetching) return;
    ensureMeshEdgesOn();
    selecting = true;
    host.setAnomalySelectMode?.(true);
    modal.classList.add('hidden');
    banner?.classList.add('visible');
    if (bannerText) {
      bannerText.textContent =
        `Click mesh Nodes to select (${nodes.length}/${MAX_NODES}).`;
    }
  }

  function addNodeFromHit(hit) {
    if (!hit) {
      setStatus('Click on the mesh (zoom in if edges are sparse).');
      return;
    }
    if (nodes.length >= MAX_NODES) {
      setStatus(`Maximum of ${MAX_NODES} Nodes reached.`);
      return;
    }
    const lon = wrapLon(hit.lon);
    const lat = hit.lat;
    const dup = nodes.find(n => {
      let dx = n.lon - lon;
      if (dx > 180) dx -= 360;
      if (dx < -180) dx += 360;
      const dy = n.lat - lat;
      return dx * dx + dy * dy < 1e-12;
    });
    if (dup) {
      setStatus(`Node already selected (#${dup.id}).`);
      return;
    }
    nodes.push({id: nextId++, lon, lat});
    histResult = null;
    mpResult = null;
    resultsEl?.classList.add('hidden');
    mpResultsEl?.classList.add('hidden');
    if (histStatsEl) {
      histStatsEl.classList.add('hidden');
      histStatsEl.innerHTML = '';
    }
    updateNodeUi();
    if (bannerText) {
      bannerText.textContent =
        nodes.length >= MAX_NODES
          ? `Maximum of ${MAX_NODES} Nodes. Click Done selecting when ready.`
          : `Click mesh Nodes to select (${nodes.length}/${MAX_NODES}).`;
    }
  }

  function focusNode(id) {
    if (!nodes.some(n => n.id === id)) return false;
    focusNodeId = id;
    if (activeTab === 'historical' && histResult) {
      if (viewModeSel) viewModeSel.value = String(id);
      drawCurrentHistogram();
    } else if (activeTab === 'multiparam') {
      if (mpNodeSelect) mpNodeSelect.value = String(id);
      updateRunButton();
    } else {
      updateNodeUi();
    }
    modal.classList.remove('hidden');
    return true;
  }

  function tryFocusNear(lon, lat) {
    if (!nodes.length) return false;
    let best = null;
    for (const n of nodes) {
      let dx = n.lon - lon;
      if (dx > 180) dx -= 360;
      if (dx < -180) dx += 360;
      const dy = n.lat - lat;
      const distSq = dx * dx + dy * dy;
      if (!best || distSq < best.distSq) best = {n, distSq};
    }
    if (!best || best.distSq > MATCH_DIST_DEG * MATCH_DIST_DEG) return false;
    return focusNode(best.n.id);
  }

  function handleMapClick(lon, lat) {
    if (selecting) {
      addNodeFromHit(host.pickMeshNode?.(lon, lat));
      return true;
    }
    // After results (or anytime Nodes are selected), clicking a highlighted
    // Node focuses it for histogram / Multi-Parameter.
    if (nodes.length) {
      return tryFocusNear(lon, lat);
    }
    return false;
  }

  function placeAtScreen(x, y) {
    const deck = host.getDeck?.();
    const vp = deck?.getViewports?.()?.[0];
    if (!vp) return;
    const [lon, lat] = vp.unproject([x, y]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    handleMapClick(lon, lat);
  }

  let ptrDown = null;
  function onPointerDown(e) {
    if (!selecting) return;
    if (e.button !== 0) return;
    ptrDown = {x: e.clientX, y: e.clientY, t: performance.now()};
  }
  function onPointerUp(e) {
    if (!selecting || !ptrDown) return;
    if (e.button !== 0) return;
    const dx = e.clientX - ptrDown.x;
    const dy = e.clientY - ptrDown.y;
    const dt = performance.now() - ptrDown.t;
    ptrDown = null;
    if (dt > 700 || dx * dx + dy * dy > CLICK_SLOP_PX * CLICK_SLOP_PX) return;
    if (e.target?.closest?.('#anomalySelectBanner')) return;
    placeAtScreen(e.clientX, e.clientY);
  }
  function onKeyDown(e) {
    if (e.key === 'Escape' && selecting) {
      e.preventDefault();
      stopSelectMode();
      setStatus('Selection cancelled.');
    }
  }

  async function resolveNodeInCycle(store, index, node) {
    const tiles = candidateLeafTiles(index, node.lon, node.lat);
    // Nodes may have been picked from a coarser (overview) mesh, so the
    // matching leaf vertex can sit slightly farther than MATCH_DIST_DEG.
    // Take the globally nearest vertex across candidate tiles instead of
    // the first one inside a tight tolerance.
    let best = null;
    for (const tile of tiles.slice(0, 8)) {
      try {
        await store.ensureGeometry(tile.z, tile.x, tile.y);
      } catch (_) {
        continue;
      }
      const entry = store.get(tile.z, tile.x, tile.y);
      if (!entry?.positions3) continue;
      const nearest = nearestVertexInEntry(entry, node.lon, node.lat);
      if (!nearest) continue;
      if (!best || nearest.distSq < best.distSq) {
        best = {z: tile.z, x: tile.x, y: tile.y, vertexIndex: nearest.vertexIndex, distSq: nearest.distSq};
      }
    }
    if (!best || best.distSq > FALLBACK_MATCH_DIST_DEG * FALLBACK_MATCH_DIST_DEG) return null;
    if (best.distSq > MATCH_DIST_DEG * MATCH_DIST_DEG) {
      console.warn('[anomaly] node matched loosely', node, Math.sqrt(best.distSq).toFixed(4), 'deg');
    }
    return {z: best.z, x: best.x, y: best.y, vertexIndex: best.vertexIndex};
  }

  async function fetchHistogram() {
    const selectedCycles = cycleRange.getRange().items;
    if (!selectedCycles.length) {
      setStatus('No forecast cycles in the selected range / type.');
      return;
    }
    const timeSel = timeRange.getRange();
    const t0 = timeSel.startIndex;
    const t1 = timeSel.endIndex;
    const viewCycle = host.getForecastCycle?.() || '';
    const viewTimestep = host.getTimestepIndex?.() ?? -1;

    fetching = true;
    abortRequested = false;
    histResult = null;
    resultsEl?.classList.add('hidden');
    updateRunButton();
    selectBtn.disabled = true;
    clearBtn.disabled = true;
    if (cycleTypeSel) cycleTypeSel.disabled = true;
    setPlotStatus(plotStatusEl, '');
    setStatus('Starting Histogram Analysis…');

    const byNode = new Map(nodes.map(n => [n.id, []]));
    const diag = {unresolved: 0, dry: 0, failed: 0};
    const currentByNode = new Map();
    const all = [];
    let cyclesOk = 0;
    const isAborted = () => abortRequested;

    try {
      for (let ci = 0; ci < selectedCycles.length; ci++) {
        if (isAborted()) throw new AnomalyAbortError();
        const cycle = selectedCycles[ci];
        setStatus(
          `Cycle ${ci + 1}/${selectedCycles.length}: ${formatCycleLabel(cycle)} — loading…`
        );

        let manifest;
        try {
          const res = await fetch(`/chl_data/${cycle}/manifest.json`, {cache: 'no-cache'});
          if (!res.ok) throw new Error(`manifest ${res.status}`);
          manifest = await res.json();
        } catch (err) {
          console.warn('[anomaly] skip cycle', cycle, err);
          continue;
        }

        const timesteps = manifest.timesteps || [];
        if (!timesteps.length) continue;
        const iStart = Math.max(0, Math.min(timesteps.length - 1, t0));
        const iEnd = Math.max(iStart, Math.min(timesteps.length - 1, t1));
        const tIndices = [];
        for (let t = iStart; t <= iEnd; t++) tIndices.push(t);

        const index = buildQuadtreeIndex(manifest);
        const store = new TileStore(`/chl_data/${cycle}`, index);

        const resolved = new Map();
        for (const node of nodes) {
          if (isAborted()) throw new AnomalyAbortError();
          const hit = await resolveNodeInCycle(store, index, node);
          if (hit) resolved.set(node.id, hit);
          else diag.unresolved += 1;
        }
        if (!resolved.size) continue;

        const byTile = new Map();
        for (const [id, hit] of resolved) {
          const key = `${hit.z},${hit.x},${hit.y}`;
          if (!byTile.has(key)) {
            byTile.set(key, {z: hit.z, x: hit.x, y: hit.y, nodes: []});
          }
          byTile.get(key).nodes.push({id, vertexIndex: hit.vertexIndex});
        }
        const tileList = [...byTile.values()];
        const isViewCycle = cycle === viewCycle;

        await mapPool(
          tIndices,
          FETCH_CONCURRENCY,
          async t => {
            if (isAborted()) throw new AnomalyAbortError();
            for (const tile of tileList) {
              let fd;
              try {
                fd = await store.ensureScalar(tile.z, tile.x, tile.y, t);
              } catch (err) {
                console.warn('[anomaly] scalar fail', cycle, tile, t, err);
                diag.failed += 1;
                continue;
              }
              for (const {id, vertexIndex} of tile.nodes) {
                const v = fd.scalar[vertexIndex];
                if (v === FILL_VALUE || !Number.isFinite(v)) {
                  diag.dry += 1;
                  continue;
                }
                const sample = {
                  value: v,
                  cycle,
                  timestep: timesteps[t],
                  leadHours: leadHours(cycle, timesteps[t])
                };
                byNode.get(id).push(sample);
                all.push(sample);
                if (isViewCycle && t === viewTimestep) {
                  currentByNode.set(id, v);
                }
              }
            }
            if (t % 12 === 0 || t === tIndices[tIndices.length - 1]) {
              setStatus(
                `Cycle ${ci + 1}/${selectedCycles.length}: ${formatCycleLabel(cycle)} — ` +
                  `timestep ${t - iStart + 1}/${tIndices.length}`
              );
            }
          },
          {isAborted}
        );
        cyclesOk += 1;
      }

      histResult = {
        byNode,
        all,
        currentByNode,
        meta: {
          cyclesTotal: selectedCycles.length,
          cyclesOk,
          nodeCount: nodes.length,
          sampleCount: all.length,
          diag,
          viewCycle,
          viewTimestep
        }
      };
      resultsEl?.classList.remove('hidden');
      focusNodeId = null;
      if (viewModeSel) viewModeSel.value = 'aggregate';
      drawCurrentHistogram();
      setStatus('');
    } catch (err) {
      if (err instanceof AnomalyAbortError) setStatus('Cancelled.');
      else {
        console.error('[anomaly]', err);
        setStatus(`Error: ${err.message || err}`);
        setPlotStatus(plotStatusEl, String(err.message || err), {error: true});
      }
    } finally {
      fetching = false;
      updateNodeUi();
    }
  }

  async function sampleFieldAt(store, manifest, hit, t, field) {
    if (!field) return null;
    if (field.manifestFlag && !manifest[field.manifestFlag]) return null;
    try {
      if (field.kind === 'scalar') {
        const fd = await store.ensureScalar(hit.z, hit.x, hit.y, t);
        const v = fd.scalar[hit.vertexIndex];
        return v === FILL_VALUE || !Number.isFinite(v) ? null : v;
      }
      const fd = await store.ensureSpeed(hit.z, hit.x, hit.y, t, field.vectorField);
      const v = fd.scalar[hit.vertexIndex];
      return v === FILL_VALUE || !Number.isFinite(v) ? null : v;
    } catch (err) {
      console.warn('[anomaly-mp] sample fail', field.id, t, err);
      return null;
    }
  }

  async function fetchMultiParam() {
    const sel = mpNodeSelect?.value || '';
    const averageMode = sel === 'average';
    const nodeId = averageMode ? null : parseInt(sel, 10);
    const targetNodes = averageMode
      ? nodes.slice()
      : nodes.filter(n => n.id === nodeId);
    if (!targetNodes.length) {
      setStatus('Select a Node (or Average of Selected Nodes) for Multi-Parameter.');
      return;
    }
    const leftField = MP_FIELDS[mpLeftAxisSel?.value] || MP_FIELDS.waterlevel;
    const rightField = MP_FIELDS[mpRightAxisSel?.value] || MP_FIELDS.windspeed;
    const selectedCycles = mpCycleRange.getRange().items;
    if (!selectedCycles.length) {
      setStatus('No forecast cycles in the selected range / type.');
      return;
    }
    const timeSel = mpTimeRange.getRange();
    const t0 = timeSel.startIndex;
    const t1 = timeSel.endIndex;

    fetching = true;
    abortRequested = false;
    mpResult = null;
    mpResultsEl?.classList.add('hidden');
    clearMpPlots();
    updateRunButton();
    selectBtn.disabled = true;
    clearBtn.disabled = true;
    if (mpCycleTypeSel) mpCycleTypeSel.disabled = true;
    if (mpNodeSelect) mpNodeSelect.disabled = true;
    if (mpLeftAxisSel) mpLeftAxisSel.disabled = true;
    if (mpRightAxisSel) mpRightAxisSel.disabled = true;
    setPlotStatus(mpPlotStatusEl, '');
    setStatus('Starting Multi-Parameter…');

    const cycleSeries = [];
    const isAborted = () => abortRequested;

    try {
      for (let ci = 0; ci < selectedCycles.length; ci++) {
        if (isAborted()) throw new AnomalyAbortError();
        const cycle = selectedCycles[ci];
        setStatus(
          `Cycle ${ci + 1}/${selectedCycles.length}: ${formatCycleLabel(cycle)} — loading…`
        );

        let manifest;
        try {
          const res = await fetch(`/chl_data/${cycle}/manifest.json`, {cache: 'no-cache'});
          if (!res.ok) throw new Error(`manifest ${res.status}`);
          manifest = await res.json();
        } catch (err) {
          console.warn('[anomaly-mp] skip cycle', cycle, err);
          continue;
        }

        const timesteps = manifest.timesteps || [];
        if (!timesteps.length) continue;

        const iStart = Math.max(0, Math.min(timesteps.length - 1, t0));
        const iEnd = Math.max(iStart, Math.min(timesteps.length - 1, t1));
        const tIndices = [];
        for (let t = iStart; t <= iEnd; t++) tIndices.push(t);

        const index = buildQuadtreeIndex(manifest);
        const store = new TileStore(`/chl_data/${cycle}`, index);

        const hits = [];
        for (const node of targetNodes) {
          if (isAborted()) throw new AnomalyAbortError();
          const hit = await resolveNodeInCycle(store, index, node);
          if (hit) hits.push({node, hit});
        }
        if (!hits.length) {
          console.warn('[anomaly-mp] no Nodes resolved', cycle);
          continue;
        }

        const times = [];
        const left = [];
        const right = [];

        await mapPool(
          tIndices,
          FETCH_CONCURRENCY,
          async (t, idx) => {
            if (isAborted()) throw new AnomalyAbortError();
            times[idx] = isoTimestep(timesteps[t]);
            const leftVals = [];
            const rightVals = [];
            for (const {hit} of hits) {
              const lv = await sampleFieldAt(store, manifest, hit, t, leftField);
              const rv = await sampleFieldAt(store, manifest, hit, t, rightField);
              if (lv != null) leftVals.push(lv);
              if (rv != null) rightVals.push(rv);
            }
            left[idx] = leftVals.length
              ? leftVals.reduce((a, b) => a + b, 0) / leftVals.length
              : null;
            right[idx] = rightVals.length
              ? rightVals.reduce((a, b) => a + b, 0) / rightVals.length
              : null;
            if (idx % 12 === 0 || idx === tIndices.length - 1) {
              setStatus(
                `Cycle ${ci + 1}/${selectedCycles.length}: ${formatCycleLabel(cycle)} — ` +
                  `timestep ${idx + 1}/${tIndices.length}`
              );
            }
          },
          {isAborted}
        );

        cycleSeries.push({cycle, times, left, right});
      }

      if (!cycleSeries.length) {
        setStatus('No Multi-Parameter series could be built.');
        setPlotStatus(mpPlotStatusEl, 'No data for the selected Node / cycles.', {error: true});
        return;
      }

      mpResult = {
        mode: averageMode ? 'average' : 'node',
        nodeId: averageMode ? null : targetNodes[0].id,
        nodeCount: targetNodes.length,
        cycles: cycleSeries,
        leftField,
        rightField
      };
      if (!averageMode) focusNodeId = targetNodes[0].id;
      mpResultsEl?.classList.remove('hidden');
      drawMpPlots();
      setStatus('');
    } catch (err) {
      if (err instanceof AnomalyAbortError) setStatus('Cancelled.');
      else {
        console.error('[anomaly-mp]', err);
        setStatus(`Error: ${err.message || err}`);
        setPlotStatus(mpPlotStatusEl, String(err.message || err), {error: true});
      }
    } finally {
      fetching = false;
      if (mpLeftAxisSel) mpLeftAxisSel.disabled = false;
      if (mpRightAxisSel) mpRightAxisSel.disabled = false;
      updateNodeUi();
    }
  }

  tabHistorical?.addEventListener('click', () => setActiveTab('historical'));
  tabMultiparam?.addEventListener('click', () => setActiveTab('multiparam'));
  cycleTypeSel?.addEventListener('change', () => refreshHistoricalOptions({resetRange: true}));
  mpCycleTypeSel?.addEventListener('change', () => refreshMpOptions({resetRange: true}));
  mpNodeSelect?.addEventListener('change', () => {
    const sel = mpNodeSelect.value || '';
    if (sel && sel !== 'average') {
      const id = parseInt(sel, 10);
      if (Number.isFinite(id)) focusNodeId = id;
    }
    host.setAnomalyMarkers?.(markerPayload());
    updateRunButton();
  });

  btn.addEventListener('click', () => openModal());
  closeBtn?.addEventListener('click', () => closeModal());
  cancelBtn?.addEventListener('click', () => closeModal());
  selectBtn?.addEventListener('click', () => startSelectMode());
  selectDoneBtn?.addEventListener('click', () => {
    stopSelectMode();
    setStatus(nodes.length ? `${nodes.length} Node(s) selected.` : 'No Nodes selected.');
  });
  clearBtn?.addEventListener('click', () => {
    if (fetching) return;
    nodes = [];
    nextId = 1;
    histResult = null;
    mpResult = null;
    focusNodeId = null;
    resultsEl?.classList.add('hidden');
    mpResultsEl?.classList.add('hidden');
    if (histStatsEl) {
      histStatsEl.classList.add('hidden');
      histStatsEl.innerHTML = '';
    }
    try {
      if (plotEl) Plotly.purge(plotEl);
    } catch (_) {
      /* empty */
    }
    clearMpPlots();
    setPlotStatus(plotStatusEl, '');
    setPlotStatus(mpPlotStatusEl, '');
    updateNodeUi();
    setStatus('Cleared.');
  });
  histRunBtn?.addEventListener('click', () => {
    fetchHistogram().catch(err => console.error(err));
  });
  mpRunBtn?.addEventListener('click', () => {
    fetchMultiParam().catch(err => console.error(err));
  });
  viewModeSel?.addEventListener('change', () => {
    if (!histResult) return;
    drawCurrentHistogram();
  });

  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('pointerup', onPointerUp, true);
  window.addEventListener('keydown', onKeyDown);

  return {
    handleMapClick,
    isSelecting: () => selecting,
    hasResult: () => Boolean(histResult || mpResult)
  };
}
